import { z } from 'zod';

/**
 * Customer = tenant of the application (ADR-0005). NOT a supplier/sender of
 * invoices; that data lives inside `InvoiceBlob.seller`. Each invoice belongs
 * to exactly one customer (`invoices.customer_id`).
 */
export const TaxDefaultsSchema = z
  .object({
    /** Default invoice currency for this customer (ISO 4217). */
    currency: z.string().length(3).optional(),
    /** Default VAT rate in percent (e.g. 19, 7). */
    defaultVatRate: z.number().min(0).max(100).optional(),
    /** ISO country for fallback country detection. */
    country: z
      .string()
      .length(2)
      .regex(/^[A-Z]{2}$/)
      .optional(),
  })
  .strict();
export type TaxDefaults = z.infer<typeof TaxDefaultsSchema>;

const MailboxBaseSchema = z.object({
  scanEmail: z.string().trim().email().max(255),
  mailbox: z.string().trim().min(1).max(255).default('INBOX'),
  lastScannedAt: z.string().datetime().nullable().optional(),
});

export const CustomerMailboxAuthMethodEnum = z.enum([
  'imap-password',
  'microsoft-graph',
  'gmail-api-oauth',
]);
export type CustomerMailboxAuthMethod = z.infer<typeof CustomerMailboxAuthMethodEnum>;

export const CustomerSapSystemEnum = z.enum(['none', 'sap-s4hana', 'sap-erp-6']);
export type CustomerSapSystem = z.infer<typeof CustomerSapSystemEnum>;

export const CustomerSapAuthMethodEnum = z.enum([
  'basic',
  'oauth2-client-credentials',
  'nango-proxy',
]);
export type CustomerSapAuthMethod = z.infer<typeof CustomerSapAuthMethodEnum>;

const CustomerSapConfigBaseSchema = z.object({
  baseUrl: z.string().trim().url(),
  client: z.string().trim().min(1).max(10).optional(),
  companyCode: z.string().trim().min(1).max(10).optional(),
});

export const CustomerSapBasicConfigSchema = CustomerSapConfigBaseSchema.extend({
  authMethod: z.literal('basic').default('basic'),
  username: z.string().trim().min(1).max(255),
  hasPassword: z.boolean(),
  password: z.string().min(1).max(500).optional(),
});
export type CustomerSapBasicConfig = z.infer<typeof CustomerSapBasicConfigSchema>;

export const CustomerSapOauth2ClientCredentialsConfigSchema = CustomerSapConfigBaseSchema.extend({
  authMethod: z.literal('oauth2-client-credentials'),
  oauthTokenUrl: z.string().trim().url(),
  oauthClientId: z.string().trim().min(1).max(255),
  oauthScopes: z.string().trim().min(1).max(1000).optional(),
  hasOauthClientSecret: z.boolean(),
  oauthClientSecret: z.string().min(1).max(4000).optional(),
});
export type CustomerSapOauth2ClientCredentialsConfig = z.infer<
  typeof CustomerSapOauth2ClientCredentialsConfigSchema
>;

export const CustomerSapNangoProxyConfigSchema = CustomerSapConfigBaseSchema.extend({
  authMethod: z.literal('nango-proxy'),
  providerConfigKey: z.string().trim().min(1).max(255),
  connectionId: z.string().trim().min(1).max(255),
  hasNangoSecret: z.boolean(),
  nangoSecret: z.string().min(1).max(4000).optional(),
});
export type CustomerSapNangoProxyConfig = z.infer<typeof CustomerSapNangoProxyConfigSchema>;

export const CustomerSapConfigSchema = z.union([
  CustomerSapBasicConfigSchema,
  CustomerSapOauth2ClientCredentialsConfigSchema,
  CustomerSapNangoProxyConfigSchema,
]);
export type CustomerSapConfig = z.infer<typeof CustomerSapConfigSchema>;

export const CustomerSapBasicConfigInputSchema = CustomerSapConfigBaseSchema.extend({
  authMethod: z.literal('basic').default('basic'),
  username: z.string().trim().min(1).max(255),
  password: z.string().min(1).max(500).optional(),
});
export type CustomerSapBasicConfigInput = z.infer<typeof CustomerSapBasicConfigInputSchema>;

export const CustomerSapOauth2ClientCredentialsConfigInputSchema =
  CustomerSapConfigBaseSchema.extend({
    authMethod: z.literal('oauth2-client-credentials'),
    oauthTokenUrl: z.string().trim().url(),
    oauthClientId: z.string().trim().min(1).max(255),
    oauthClientSecret: z.string().min(1).max(4000).optional(),
    oauthScopes: z.string().trim().min(1).max(1000).optional(),
  });
export type CustomerSapOauth2ClientCredentialsConfigInput = z.infer<
  typeof CustomerSapOauth2ClientCredentialsConfigInputSchema
>;

export const CustomerSapNangoProxyConfigInputSchema = CustomerSapConfigBaseSchema.extend({
  authMethod: z.literal('nango-proxy'),
  providerConfigKey: z.string().trim().min(1).max(255),
  connectionId: z.string().trim().min(1).max(255),
  nangoSecret: z.string().min(1).max(4000).optional(),
});
export type CustomerSapNangoProxyConfigInput = z.infer<
  typeof CustomerSapNangoProxyConfigInputSchema
>;

export const CustomerSapConfigInputSchema = z.union([
  CustomerSapBasicConfigInputSchema,
  CustomerSapOauth2ClientCredentialsConfigInputSchema,
  CustomerSapNangoProxyConfigInputSchema,
]);
export type CustomerSapConfigInput = z.infer<typeof CustomerSapConfigInputSchema>;

export const CustomerMailboxPasswordConfigSchema = MailboxBaseSchema.extend({
  authMethod: z.literal('imap-password'),
  host: z.string().trim().min(1).max(255),
  port: z.number().int().min(1).max(65535),
  secure: z.boolean(),
  username: z.string().trim().min(1).max(255),
  hasPassword: z.boolean(),
  lastSeenUid: z.number().int().nonnegative().nullable().optional(),
});
export type CustomerMailboxPasswordConfig = z.infer<typeof CustomerMailboxPasswordConfigSchema>;

export const CustomerMailboxMicrosoftGraphConfigSchema = MailboxBaseSchema.extend({
  authMethod: z.literal('microsoft-graph'),
  oauthTenant: z.string().trim().min(1).max(255).default('consumers'),
  oauthClientId: z.string().trim().min(1).max(255),
  hasRefreshToken: z.boolean(),
});
export type CustomerMailboxMicrosoftGraphConfig = z.infer<
  typeof CustomerMailboxMicrosoftGraphConfigSchema
>;

export const CustomerMailboxGmailApiOauthConfigSchema = MailboxBaseSchema.extend({
  authMethod: z.literal('gmail-api-oauth'),
  oauthClientId: z.string().trim().min(1).max(255),
  oauthClientSecret: z.string().min(1).max(4000).optional(),
  hasClientSecret: z.boolean(),
  hasRefreshToken: z.boolean(),
  lastSeenUid: z.number().int().nonnegative().nullable().optional(),
});
export type CustomerMailboxGmailApiOauthConfig = z.infer<
  typeof CustomerMailboxGmailApiOauthConfigSchema
>;

export const CustomerMailboxConfigSchema = z.discriminatedUnion('authMethod', [
  CustomerMailboxPasswordConfigSchema,
  CustomerMailboxMicrosoftGraphConfigSchema,
  CustomerMailboxGmailApiOauthConfigSchema,
]);
export type CustomerMailboxConfig = z.infer<typeof CustomerMailboxConfigSchema>;

export const CustomerMailboxPasswordConfigInputSchema = z.object({
  authMethod: z.literal('imap-password'),
  scanEmail: z.string().trim().email().max(255),
  host: z.string().trim().min(1).max(255),
  port: z.number().int().min(1).max(65535),
  secure: z.boolean(),
  username: z.string().trim().min(1).max(255),
  password: z.string().min(1).max(500).optional(),
  mailbox: z.string().trim().min(1).max(255).default('INBOX'),
});
export type CustomerMailboxPasswordConfigInput = z.infer<
  typeof CustomerMailboxPasswordConfigInputSchema
>;

export const CustomerMailboxMicrosoftGraphConfigInputSchema = z.object({
  authMethod: z.literal('microsoft-graph'),
  scanEmail: z.string().trim().email().max(255),
  oauthTenant: z.string().trim().min(1).max(255).default('consumers'),
  oauthClientId: z.string().trim().min(1).max(255),
  oauthRefreshToken: z.string().min(1).max(4000).optional(),
  mailbox: z.string().trim().min(1).max(255).default('INBOX'),
});
export type CustomerMailboxMicrosoftGraphConfigInput = z.infer<
  typeof CustomerMailboxMicrosoftGraphConfigInputSchema
>;

export const CustomerMailboxGmailApiOauthConfigInputSchema = z.object({
  authMethod: z.literal('gmail-api-oauth'),
  scanEmail: z.string().trim().email().max(255),
  oauthClientId: z.string().trim().min(1).max(255),
  oauthClientSecret: z.string().min(1).max(4000).optional(),
  oauthRefreshToken: z.string().min(1).max(4000).optional(),
  mailbox: z.string().trim().min(1).max(255).default('INBOX'),
});
export type CustomerMailboxGmailApiOauthConfigInput = z.infer<
  typeof CustomerMailboxGmailApiOauthConfigInputSchema
>;

export const CustomerMailboxConfigInputSchema = z.discriminatedUnion('authMethod', [
  CustomerMailboxPasswordConfigInputSchema,
  CustomerMailboxMicrosoftGraphConfigInputSchema,
  CustomerMailboxGmailApiOauthConfigInputSchema,
]);
export type CustomerMailboxConfigInput = z.infer<typeof CustomerMailboxConfigInputSchema>;

export const CustomerSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  contactName: z.string().optional(),
  contactEmail: z.string().email().optional(),
  sapSystem: CustomerSapSystemEnum.default('none'),
  sapConfig: CustomerSapConfigSchema.optional(),
  mailboxConfig: CustomerMailboxConfigSchema.optional(),
  taxDefaults: TaxDefaultsSchema.optional(),
  /**
   * ISO timestamp when the customer was soft-deleted (#57). `null` for active
   * customers; never gets reset to `undefined` once set so reads can rely on
   * `archivedAt === null` as the active-customer guard.
   */
  archivedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().optional(),
});
export type Customer = z.infer<typeof CustomerSchema>;

export const CreateCustomerRequestSchema = z
  .object({
    name: z.string().trim().min(1),
    contactName: z.string().trim().max(200).optional(),
    contactEmail: z.string().email().optional(),
    sapSystem: CustomerSapSystemEnum.optional(),
    sapConfig: CustomerSapConfigInputSchema.optional(),
    mailboxConfig: CustomerMailboxConfigInputSchema.optional(),
    taxDefaults: TaxDefaultsSchema.optional(),
  })
  .superRefine((value, ctx) => {
    const sapSystem = value.sapSystem ?? 'none';
    if (sapSystem === 'none') {
      if (value.sapConfig !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sapConfig'],
          message: 'SAP-Konfiguration ist nur mit aktivem SAP-System erlaubt.',
        });
      }
      return;
    }

    if (!value.sapConfig) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sapConfig'],
        message: 'SAP-Konfiguration ist erforderlich, wenn ein SAP-System aktiv ist.',
      });
      return;
    }

    if (value.sapConfig.authMethod === 'basic' && !value.sapConfig.password) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sapConfig', 'password'],
        message: 'SAP-Passwort ist erforderlich.',
      });
    }
    if (
      value.sapConfig.authMethod === 'oauth2-client-credentials' &&
      !value.sapConfig.oauthClientSecret
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sapConfig', 'oauthClientSecret'],
        message: 'SAP OAuth2 Client Secret ist erforderlich.',
      });
    }
    if (value.sapConfig.authMethod === 'nango-proxy' && !value.sapConfig.nangoSecret) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sapConfig', 'nangoSecret'],
        message: 'Nango Secret Key ist erforderlich.',
      });
    }
  });
export type CreateCustomerRequest = z.infer<typeof CreateCustomerRequestSchema>;

export const UpdateCustomerRequestSchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    contactName: z.string().trim().max(200).nullable().optional(),
    contactEmail: z.string().email().nullable().optional(),
    sapSystem: CustomerSapSystemEnum.optional(),
    sapConfig: CustomerSapConfigInputSchema.nullable().optional(),
    mailboxConfig: CustomerMailboxConfigInputSchema.nullable().optional(),
    taxDefaults: TaxDefaultsSchema.nullable().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.sapSystem === 'none' && value.sapConfig && value.sapConfig !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sapConfig'],
        message: 'SAP-Konfiguration ist nur mit aktivem SAP-System erlaubt.',
      });
    }
    if (value.sapSystem && value.sapSystem !== 'none' && value.sapConfig === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sapConfig'],
        message: 'SAP-Konfiguration kann bei aktivem SAP-System nicht entfernt werden.',
      });
    }
  });
export type UpdateCustomerRequest = z.infer<typeof UpdateCustomerRequestSchema>;

/**
 * Compact customer view returned by `GET /customers` (#42). Used by the
 * invoice-list filter dropdown and the upload-dialog customer-select.
 * Deliberately narrow - we never want to leak the contact / tax-defaults
 * surface to widgets that only need an `<option>` label.
 */
export const CustomerOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type CustomerOption = z.infer<typeof CustomerOptionSchema>;

export const CustomerOptionListSchema = z.object({
  items: z.array(CustomerOptionSchema),
});
export type CustomerOptionList = z.infer<typeof CustomerOptionListSchema>;

export const CustomerOptionQuerySchema = z.object({
  q: z.string().trim().min(1).max(200).optional(),
});
export type CustomerOptionQuery = z.infer<typeof CustomerOptionQuerySchema>;

export const CustomerSupplierOptionKindEnum = z.enum(['mapped', 'observed']);
export type CustomerSupplierOptionKind = z.infer<typeof CustomerSupplierOptionKindEnum>;

export const CustomerSupplierOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: CustomerSupplierOptionKindEnum,
});
export type CustomerSupplierOption = z.infer<typeof CustomerSupplierOptionSchema>;

export const CustomerSupplierOptionListSchema = z.object({
  items: z.array(CustomerSupplierOptionSchema),
});
export type CustomerSupplierOptionList = z.infer<typeof CustomerSupplierOptionListSchema>;

export const CustomerSupplierOptionQuerySchema = z.object({
  q: z.string().trim().min(1).max(200).optional(),
});
export type CustomerSupplierOptionQuery = z.infer<typeof CustomerSupplierOptionQuerySchema>;

/**
 * Admin-CRUD list-row used by the customer-management table (#57). Extends
 * `CustomerSchema` with aggregate counters (#invoices, last invoice timestamp,
 * #rules) so the table can render status without a per-row N+1 fetch.
 */
export const CustomerListItemSchema = CustomerSchema.extend({
  invoiceCount: z.number().int().nonnegative(),
  /** ISO timestamp of the most recent invoice; `null` when the customer has none. */
  lastInvoiceAt: z.string().datetime().nullable(),
  ruleCount: z.number().int().nonnegative(),
});
export type CustomerListItem = z.infer<typeof CustomerListItemSchema>;

export const CustomerListResponseSchema = z.object({
  items: z.array(CustomerListItemSchema),
});
export type CustomerListResponse = z.infer<typeof CustomerListResponseSchema>;

export const CustomerMailboxTestSyncResponseSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  scannedAt: z.string().datetime(),
  messagesSeen: z.number().int().nonnegative(),
  attachmentsProcessed: z.number().int().nonnegative(),
  attachmentsSkipped: z.number().int().nonnegative(),
  steps: z
    .array(
      z.object({
        status: z.enum(['info', 'success', 'skipped', 'error']),
        code: z.string().min(1),
        message: z.string().min(1),
        detail: z.string().min(1).optional(),
      }),
    )
    .default([]),
});
export type CustomerMailboxTestSyncResponse = z.infer<typeof CustomerMailboxTestSyncResponseSchema>;
export type CustomerMailboxTestSyncStep = CustomerMailboxTestSyncResponse['steps'][number];

export const CustomerSapConnectionTestResponseSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  system: z.enum(['sap-s4hana', 'sap-erp-6']),
  authMethod: CustomerSapAuthMethodEnum,
  checkedAt: z.string().datetime(),
  ok: z.boolean(),
  status: z.number().int().min(100).max(599).optional(),
  message: z.string().optional(),
});
export type CustomerSapConnectionTestResponse = z.infer<
  typeof CustomerSapConnectionTestResponseSchema
>;

/**
 * Query shape for `GET /customers/admin`. `includeArchived=true` includes
 * soft-deleted rows; default keeps the list clean for everyday use.
 */
export const CustomerListAdminQuerySchema = z.object({
  includeArchived: z
    .union([z.literal('true'), z.literal('false'), z.boolean()])
    .optional()
    .transform((value) => value === true || value === 'true'),
});
export type CustomerListAdminQuery = z.infer<typeof CustomerListAdminQuerySchema>;

export const CustomerMailboxMicrosoftDeviceCodeStartRequestSchema = z.object({
  tenant: z.string().trim().min(1).max(255),
  clientId: z.string().trim().min(1).max(255),
});
export type CustomerMailboxMicrosoftDeviceCodeStartRequest = z.infer<
  typeof CustomerMailboxMicrosoftDeviceCodeStartRequestSchema
>;

export const CustomerMailboxMicrosoftDeviceCodeResponseSchema = z.object({
  deviceCode: z.string().min(1),
  userCode: z.string().min(1),
  verificationUri: z.string().url(),
  expiresIn: z.number().int().positive(),
  interval: z.number().int().positive(),
  message: z.string().min(1),
});
export type CustomerMailboxMicrosoftDeviceCodeResponse = z.infer<
  typeof CustomerMailboxMicrosoftDeviceCodeResponseSchema
>;

export const CustomerMailboxMicrosoftDeviceCodePollRequestSchema = z.object({
  tenant: z.string().trim().min(1).max(255),
  clientId: z.string().trim().min(1).max(255),
  deviceCode: z.string().min(1),
});
export type CustomerMailboxMicrosoftDeviceCodePollRequest = z.infer<
  typeof CustomerMailboxMicrosoftDeviceCodePollRequestSchema
>;

export const CustomerMailboxMicrosoftDeviceCodePollResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('pending'),
    interval: z.number().int().positive(),
  }),
  z.object({
    status: z.literal('success'),
    refreshToken: z.string().min(1),
    expiresIn: z.number().int().positive(),
    scope: z.string().min(1),
  }),
]);
export type CustomerMailboxMicrosoftDeviceCodePollResponse = z.infer<
  typeof CustomerMailboxMicrosoftDeviceCodePollResponseSchema
>;

export const CustomerMailboxGoogleDeviceCodeStartRequestSchema = z.object({
  clientId: z.string().trim().min(1).max(255),
});
export type CustomerMailboxGoogleDeviceCodeStartRequest = z.infer<
  typeof CustomerMailboxGoogleDeviceCodeStartRequestSchema
>;

export const CustomerMailboxGoogleDeviceCodeResponseSchema = z.object({
  deviceCode: z.string().min(1),
  userCode: z.string().min(1),
  verificationUri: z.string().url(),
  expiresIn: z.number().int().positive(),
  interval: z.number().int().positive(),
});
export type CustomerMailboxGoogleDeviceCodeResponse = z.infer<
  typeof CustomerMailboxGoogleDeviceCodeResponseSchema
>;

export const CustomerMailboxGoogleDeviceCodePollRequestSchema = z.object({
  clientId: z.string().trim().min(1).max(255),
  deviceCode: z.string().min(1),
});
export type CustomerMailboxGoogleDeviceCodePollRequest = z.infer<
  typeof CustomerMailboxGoogleDeviceCodePollRequestSchema
>;

export const CustomerMailboxGoogleDeviceCodePollResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('pending'),
    interval: z.number().int().positive(),
  }),
  z.object({
    status: z.literal('success'),
    refreshToken: z.string().min(1),
    expiresIn: z.number().int().positive(),
    scope: z.string().min(1),
  }),
]);
export type CustomerMailboxGoogleDeviceCodePollResponse = z.infer<
  typeof CustomerMailboxGoogleDeviceCodePollResponseSchema
>;
